const LAYOUT_COLUMN_SNAP_PERCENT = 12.5;
const LAYOUT_COLUMN_MIN_WIDTH_PERCENT = 1;

function snapBoundaryPercent(boundaryPercent: number): number {
  return Math.round(
    Math.round(boundaryPercent / LAYOUT_COLUMN_SNAP_PERCENT) * LAYOUT_COLUMN_SNAP_PERCENT,
  );
}

function symmetricResizeEligible(columnCount: number, dividerIndex: number): boolean {
  if (columnCount === 3) {
    return dividerIndex === 0 || dividerIndex === 1;
  }

  return false;
}

function symmetricOuterColumnDelta(
  startWidths: readonly number[],
  nextWidths: readonly number[],
  dividerIndex: number,
): number {
  const lastIndex = startWidths.length - 1;
  if (dividerIndex === 0) {
    return nextWidths[0] - startWidths[0];
  }
  return nextWidths[lastIndex] - startWidths[lastIndex];
}

function applySymmetricOuterColumnWidths(
  startWidths: readonly number[],
  dividerIndex: number,
  nextWidths: number[],
): number[] {
  const lastIndex = startWidths.length - 1;
  const deltaOuter = symmetricOuterColumnDelta(startWidths, nextWidths, dividerIndex);
  const next = [...startWidths];
  next[0] = Math.max(
    LAYOUT_COLUMN_MIN_WIDTH_PERCENT,
    Math.round(startWidths[0] + deltaOuter),
  );
  next[lastIndex] = Math.max(
    LAYOUT_COLUMN_MIN_WIDTH_PERCENT,
    Math.round(startWidths[lastIndex] + deltaOuter),
  );

  let innerTotal = 0;
  for (let index = 1; index < lastIndex; index += 1) {
    innerTotal += startWidths[index];
  }
  const nextInnerTotal = 100 - next[0] - next[lastIndex];
  if (nextInnerTotal < (lastIndex - 1) * LAYOUT_COLUMN_MIN_WIDTH_PERCENT) {
    return [...startWidths];
  }

  if (innerTotal <= 0 || lastIndex <= 2) {
    if (lastIndex === 2) {
      next[1] = 100 - next[0] - next[lastIndex];
      if (next[1] < LAYOUT_COLUMN_MIN_WIDTH_PERCENT) {
        return [...startWidths];
      }
      return next;
    }
    return [...startWidths];
  }

  let distributed = 0;
  for (let index = 1; index < lastIndex; index += 1) {
    const share = Math.round((startWidths[index] / innerTotal) * nextInnerTotal);
    next[index] = Math.max(LAYOUT_COLUMN_MIN_WIDTH_PERCENT, share);
    distributed += next[index];
  }

  const remainder = 100 - next[0] - next[lastIndex] - distributed;
  if (remainder !== 0 && lastIndex >= 2) {
    const centerIndex = Math.floor(lastIndex / 2);
    next[centerIndex] = Math.max(LAYOUT_COLUMN_MIN_WIDTH_PERCENT, next[centerIndex] + remainder);
  }

  const sum = next.reduce((total, width) => total + width, 0);
  if (sum !== 100) {
    const centerIndex = Math.floor(lastIndex / 2);
    next[centerIndex] = Math.max(LAYOUT_COLUMN_MIN_WIDTH_PERCENT, next[centerIndex] + (100 - sum));
  }

  return next;
}

export function emailBuilderLayoutColumnResizeSymmetricHintEligible(
  columnCount: number,
  dividerIndex: number,
): boolean {
  return symmetricResizeEligible(columnCount, dividerIndex);
}

export function computeEmailBuilderLayoutColumnResizeWidths(args: {
  widths: readonly number[];
  dividerIndex: number;
  deltaPercent: number;
  shiftKey: boolean;
  altKey?: boolean;
}): number[] {
  const { widths, dividerIndex, deltaPercent, shiftKey, altKey = false } = args;
  const left = widths[dividerIndex];
  const right = widths[dividerIndex + 1];
  const pairSum = left + right;

  let nextLeft = left + deltaPercent;

  if (shiftKey) {
    let prefix = 0;
    for (let index = 0; index < dividerIndex; index += 1) {
      prefix += widths[index];
    }
    nextLeft = snapBoundaryPercent(prefix + nextLeft) - prefix;
  }

  nextLeft = Math.max(
    LAYOUT_COLUMN_MIN_WIDTH_PERCENT,
    Math.min(pairSum - LAYOUT_COLUMN_MIN_WIDTH_PERCENT, Math.round(nextLeft)),
  );

  if (nextLeft === left) {
    return [...widths];
  }

  const pairResized = [...widths];
  pairResized[dividerIndex] = nextLeft;
  pairResized[dividerIndex + 1] = pairSum - nextLeft;

  if (!altKey || !symmetricResizeEligible(widths.length, dividerIndex)) {
    return pairResized;
  }

  if (shiftKey) {
    let prefix = 0;
    for (let index = 0; index < dividerIndex; index += 1) {
      prefix += widths[index];
    }
    const snappedBoundary = snapBoundaryPercent(prefix + pairResized[dividerIndex]);
    const snappedLeft = snappedBoundary - prefix;
    pairResized[dividerIndex] = snappedLeft;
    pairResized[dividerIndex + 1] = pairSum - snappedLeft;
  }

  return applySymmetricOuterColumnWidths(widths, dividerIndex, pairResized);
}

/** CSS `left` for a point at `prefixPercent` into the fr track, plus prior gaps (+ optional gap fraction). */
function layoutColumnTrackOffsetCss(
  prefixPercent: number,
  priorGapCount: number,
  gapPx: number,
  gapCount: number,
  extraGapPx = 0,
): string {
  return `calc(${prefixPercent / 100} * (100% - ${gapCount * gapPx}px) + ${
    priorGapCount * gapPx + extraGapPx
  }px)`;
}

/** CSS `left` for the divider after column `dividerIndex` (0-based): centered in the grid gap. */
export function emailBuilderLayoutColumnDividerOffsetCss(
  widths: readonly number[],
  dividerIndex: number,
  gapPx: number,
): string {
  const prefix = widths.slice(0, dividerIndex + 1).reduce((total, width) => total + width, 0);
  return layoutColumnTrackOffsetCss(
    prefix,
    dividerIndex,
    gapPx,
    Math.max(0, widths.length - 1),
    gapPx / 2,
  );
}

/** CSS `left` for the center of column `columnIndex` (for live % labels). */
export function emailBuilderLayoutColumnCenterOffsetCss(
  widths: readonly number[],
  columnIndex: number,
  gapPx: number,
): string {
  const prefix =
    widths.slice(0, columnIndex).reduce((total, width) => total + width, 0) +
    widths[columnIndex] / 2;
  return layoutColumnTrackOffsetCss(
    prefix,
    columnIndex,
    gapPx,
    Math.max(0, widths.length - 1),
  );
}
